import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Square, ArrowDown, Image, Globe, X, Pencil, ArrowRight, Zap, FileCode, Camera, Eye, Brain, FileText, Pencil as PencilIcon, Activity, Layers } from 'lucide-react';
import MessageBubble from './MessageBubble';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL || '';
const AI_AVATAR = 'https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHdvbWFuJTIwaGVhZHNob3QlMjBwcm9mZXNzaW9uYWwlMjBlbGVnYW50JTIwZGFyayUyMGhhaXIlMjBuZXV0cmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYyMTI0ODB8MA&ixlib=rb-4.1.0&q=85&w=100&h=100&fit=crop';

const CHAT_T = {
  en: {
    describeIdea: 'Describe your project idea',
    aiHelp: "I'll analyze it, suggest designs, and generate code for you.",
    placeholderRef: 'Describe what you want to build with these references...',
    placeholder: 'Describe your idea...',
    shiftEnter: 'Shift+Enter new line',
    modify: 'Modify',
    nextDesign: 'Design',
    nextBuild: 'Build',
    modifyHint: 'Select a section in the preview to modify',
    modifyHintFr: 'Selectionnez une section dans la preview',
  },
  fr: {
    describeIdea: 'Decrivez votre idee de projet',
    aiHelp: "Je vais l'analyser, proposer des designs, et generer le code pour vous.",
    placeholderRef: 'Decrivez ce que vous voulez construire avec ces references...',
    placeholder: 'Decrivez votre idee...',
    shiftEnter: 'Shift+Enter nouvelle ligne',
    modify: 'Modifier',
    nextDesign: 'Design',
    nextBuild: 'Build',
    modifyHint: 'Selectionnez une section dans la preview',
    modifyHintFr: 'Selectionnez une section dans la preview',
  },
};

export default function ChatPanel({ activeChatId, onNewChat, onChatCreated, user, onAIResponse, sendRef, onLangDetected, lang = 'en', pipelineStage = 'wireframe', modifyMode, onModifyMode, selectedSection, onClearSection, onApplySection, agentRoster = [], activeAgent, onSwitchAgent, onScrapeTrigger, onStreamingChange }) {
  const ct = CHAT_T[lang] || CHAT_T.en;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  // Live progress phase: { phase, label } — set by SSE 'status' events.
  // Cleared once the first text chunk arrives.
  const [liveStatus, setLiveStatus] = useState(null);
  // Multi-agent pipeline state — list of { id, name, role, model, status, icon, char_count }
  // Driven by `agent_pipeline` SSE events. Non-null = show the timeline UI
  // instead of (or above) the basic live-status bubble.
  const [agentPipeline, setAgentPipeline] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]);  // [{preview, base64, name}]
  const [refUrl, setRefUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);
  const [pendingModify, setPendingModify] = useState(false);
  const [pendingApply, setPendingApply] = useState(null); // { heading: string } when waiting for section replacement
  // Aria edit-proposal map keyed by edit_id — fed by `edit_proposed` SSE events
  // and `loadMessages()` on chat open. Each entry feeds the inline approval
  // card that replaces `[[aria-edit:ID]]` placeholders in message content.
  const [editMap, setEditMap] = useState({});
  const fileInputRef = useRef(null);

  const chatStarted = messages.length > 0 || isStreaming;

  // Notify parent when streaming state changes (so the pipeline can guard against
  // double-clicks on NEXT etc.)
  useEffect(() => {
    if (onStreamingChange) onStreamingChange(isStreaming);
  }, [isStreaming, onStreamingChange]);

  // When a section is selected in the preview, unlock input for user to type
  useEffect(() => {
    if (selectedSection) {
      setInputLocked(false);
      setPendingModify(false);
      setTimeout(() => { if (textareaRef.current) textareaRef.current.focus(); }, 100);
    }
  }, [selectedSection]);

  useEffect(() => {
    if (activeChatId) {
      loadMessages(activeChatId);
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId]);

  useEffect(() => { scrollToBottom(); }, [messages, streamingContent]);

  const loadMessages = async (chatId) => {
    try {
      const res = await fetch(`${API}/api/chats/${chatId}/messages`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        // Parse existing messages for preview
        data.forEach(msg => {
          if (msg.role === 'assistant') onAIResponse(msg.content);
        });
      }
      // Hydrate Aria's edit proposals so historical messages still render
      // their approval cards (and show approved/rejected status correctly).
      const er = await fetch(`${API}/api/chats/${chatId}/edits`, { credentials: 'include' });
      if (er.ok) {
        const ed = await er.json();
        const map = {};
        for (const e of (ed.edits || [])) map[e.edit_id] = e;
        setEditMap(map);
      }
    } catch (err) { console.error('Failed to load messages:', err); }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 100);
  };

  const handleSend = async (messageText) => {
    let content = messageText || input.trim();
    if (!content || isStreaming) return;

    // --- Web Intelligence intercept: if active agent is web-intelligence + URL detected,
    // trigger the live scrape view instead of sending to the build LLM.
    if (activeAgent?.agent_id === 'web-intelligence' && onScrapeTrigger) {
      const urlMatch = content.match(/\b((?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?)\b/i);
      if (urlMatch) {
        const targetUrl = urlMatch[1];
        // Echo a user message + assistant confirmation locally (no LLM call)
        const userMsg = {
          message_id: `temp_${Date.now()}`,
          role: 'user', content,
          created_at: new Date().toISOString(),
        };
        const assistantMsg = {
          message_id: `ai_${Date.now()}`,
          role: 'assistant',
          content: `Kai is scraping **${targetUrl}** — watch the live extraction on the left panel.`,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg, assistantMsg]);
        setInput('');
        onScrapeTrigger(targetUrl);
        return;
      }
    }

    // If a section is selected, wrap the message with section context
    const currentSection = selectedSection;
    if (currentSection && !content.includes('[MODIFY:') && !content.includes('[APPLY]')) {
      content = `[MODIFY:${currentSection}] ${content}`;
      if (onClearSection) onClearSection();
    }

    // Detect language from user input and compute locally for immediate use
    let detectedLang = lang;
    if (onLangDetected) {
      const lower = content.toLowerCase();
      const frWords = /\b(je|tu|il|nous|vous|ils|veux|vouloir|faire|créer|avec|pour|dans|est|sont|avoir|une?|les?|des|qui|que|mais|comme|aussi|très|bien|merci|bonjour|salut|voici|voilà|oui|non|site|page)\b/;
      detectedLang = frWords.test(lower) ? 'fr' : 'en';
      onLangDetected(detectedLang);
    }

    let chatId = activeChatId;
    if (!chatId) {
      chatId = await onNewChat();
      if (!chatId) return;
    }

    // Collect attachments
    const imageData = attachedImages.map(img => img.base64);
    const urlRef = refUrl.trim() || null;

    const userMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user', content,
      created_at: new Date().toISOString(),
      attachments: { images: attachedImages.length, url: urlRef },
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setAttachedImages([]);
    setRefUrl('');
    setShowUrlInput(false);
    setIsStreaming(true);
    setStreamingContent('');
    setLiveStatus({ phase: 'init', label: 'Initializing' });
    setAgentPipeline(null);
    setInputLocked(false);
    // Track if this is a modify request for suggestion parsing
    if (content.includes('[MODIFY]')) setPendingModify(true);

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      const res = await fetch(`${API}/api/chats/${chatId}/messages`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          chat_id: chatId,
          image_data: imageData.length > 0 ? imageData : null,
          ref_url: urlRef,
          lang: detectedLang,
          agent_id: activeAgent?.agent_id || 'borntobealive-ai',
        }),
      });

      if (!res.ok) throw new Error('Failed to send message');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sawDoneEvent = false;

      const processLine = (line, accumulated) => {
        if (!line.startsWith('data: ')) return accumulated;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'text') {
            // First real text chunk → flip status off, switch to writing mode
            if (data.content && liveStatus) setLiveStatus(null);
            const updated = accumulated + data.content;
            setStreamingContent(updated);
            return updated;
          } else if (data.type === 'status') {
            setLiveStatus({ phase: data.phase, label: data.label });
            return accumulated;
          } else if (data.type === 'agent_pipeline' && Array.isArray(data.agents)) {
            // Multi-agent pipeline state update — render the live timeline.
            setAgentPipeline(data.agents);
            return accumulated;
          } else if (data.type === 'worker_status') {
            // Per-worker progress (also folded into agent_pipeline events) —
            // we keep this as a no-op since the timeline already updates.
            return accumulated;
          } else if (data.type === 'pipeline_complete') {
            // Final pipeline metadata — text was already streamed; just log.
            return accumulated;
          } else if (data.type === 'edit_proposed' && data.edit) {
            // Aria applied a precise code change. Store it keyed by edit_id
            // so the matching marker placeholder renders the receipt card.
            setEditMap(prev => ({ ...prev, [data.edit.edit_id]: data.edit }));
            // If applied successfully, broadcast so the preview iframe knows
            // to refresh once the rebuild kicks in.
            if (data.edit.status === 'applied' && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('aria-edit-applied', { detail: { edit: data.edit } }));
            }
            return accumulated;
          } else if (data.type === 'done') {
            sawDoneEvent = true;
            setLiveStatus(null);
            setAgentPipeline(null);
            setMessages(prev => [...prev, {
              message_id: data.message_id,
              role: 'assistant', content: accumulated,
              created_at: new Date().toISOString(),
            }]);
            setStreamingContent('');
            // If this is a section apply response, surgically update instead of adding new block
            if (pendingApply) {
              const heading = pendingApply.heading;
              // Clean the response — remove stage markers, keep just the section content
              let sectionContent = accumulated.replace(/\[STAGE:\w+\]/g, '').trim();
              if (onApplySection) onApplySection(heading, sectionContent);
              setPendingApply(null);
              setPendingModify(false);
              setInputLocked(true);
            } else {
              onAIResponse(accumulated);
            }
          } else if (data.type === 'error') {
            setLiveStatus(null);
            setMessages(prev => [...prev, {
              message_id: `err_${Date.now()}`,
              role: 'assistant', content: data.content || 'An error occurred. Please try again.',
              created_at: new Date().toISOString(),
            }]);
            setStreamingContent('');
            setInputLocked(false);
          }
        } catch {}
        return accumulated;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        for (const line of text.split('\n')) {
          fullContent = processLine(line, fullContent);
        }
      }
      // Safety net: if stream ended with content but no 'done' event, add it
      if (fullContent && !messages.some(m => m.content === fullContent)) {
        setMessages(prev => {
          const alreadyAdded = prev.some(m => m.role === 'assistant' && m.content === fullContent);
          if (!alreadyAdded) {
            onAIResponse(fullContent);
            if (pipelineStage !== 'code') setInputLocked(true);
            return [...prev, {
              message_id: `stream_${Date.now()}`,
              role: 'assistant', content: fullContent,
              created_at: new Date().toISOString(),
            }];
          }
          return prev;
        });
        setStreamingContent('');
      }
      // SSE-drop recovery: under heavy k8s ingress backpressure (multi-agent
      // builds taking 70-90s), the SSE connection may close before the `done`
      // event fires — even though the backend completed the build and
      // persisted the assistant message to MongoDB in a background task.
      // Full pipeline = Architect (~50s) + Workers (~60s parallel) + Merger
      // (~5s) + persist (~2s) ≈ 120s, so we poll for up to 180s to absorb the
      // long tail. We keep the pipeline timeline visible (positive framing)
      // instead of showing an alarming "connection interrupted" panic state.
      if (!sawDoneEvent && chatId) {
        setLiveStatus({ phase: 'finalizing',
                        label: 'Workers en train de finaliser ton build…' });
        await pollForRecoveredMessage(chatId, fullContent);
      }
      // If stream ended with NO content at all, it's an error — unlock
      if (!fullContent) {
        setInputLocked(false);
        setStreamingContent('');
      }
      onChatCreated();
    } catch (err) {
      console.error('Send error:', err);
      // Even on network error, the BG task may have completed and persisted.
      // Try to recover before showing the error.
      if (chatId) {
        setLiveStatus({ phase: 'finalizing',
                        label: 'Workers en train de finaliser ton build…' });
        const recovered = await pollForRecoveredMessage(chatId, '');
        if (recovered) {
          // Successfully recovered — don't show error.
          onChatCreated();
          return;
        }
      }
      setMessages(prev => [...prev, {
        message_id: `err_${Date.now()}`,
        role: 'assistant',
        content: 'Connection error. Please try again.',
        created_at: new Date().toISOString(),
      }]);
      setStreamingContent('');
      setInputLocked(false);
    } finally {
      setIsStreaming(false);
      setLiveStatus(null);
    }
  };

  // Polls /chats/{chat_id}/messages until a NEW assistant message appears.
  // Used after SSE drops (k8s ingress cuts long multi-agent builds at ~60s)
  // to pick up the message persisted by the backend's background task.
  // Returns true if a message was recovered, false otherwise.
  const pollForRecoveredMessage = async (cid, currentFullContent) => {
    if (!cid) return false;
    try {
      const beforeIds = new Set(messages.filter(m => m.role === 'assistant').map(m => m.message_id));
      // 60 attempts × 3s = 180s window. Full multi-agent pipeline = Architect
      // (~50s) + Workers (~60s) + Merger + persist ≈ 120s, so 180s gives a
      // safe 60s margin for the long tail (large prompts, GPT-4o burst-limits).
      for (let attempt = 0; attempt < 60; attempt++) {
        try {
          const r = await fetch(`${API}/api/chats/${cid}/messages`, { credentials: 'include' });
          if (r.ok) {
            const fetched = await r.json();
            const newAssistants = (fetched || []).filter(
              m => m.role === 'assistant'
                && m.content
                && !beforeIds.has(m.message_id)
                && m.content !== currentFullContent
            );
            if (newAssistants.length > 0) {
              const latest = newAssistants[newAssistants.length - 1];
              setMessages(prev => {
                const already = prev.some(m => m.message_id === latest.message_id ||
                                                m.content === latest.content);
                if (already) return prev;
                onAIResponse(latest.content);
                if (pipelineStage !== 'code') setInputLocked(true);
                return [...prev, latest];
              });
              setStreamingContent('');
              setLiveStatus(null);
              setAgentPipeline(null);
              return true;
            }
          }
        } catch (_e) { /* keep polling */ }
        // Positive-framing progress message — the build IS happening, we're
        // just waiting for the BG task to flush it to the DB. NEVER use
        // "connection interrupted" wording: it scares the user even though
        // the build is finishing fine in the background.
        const phases = [
          'Architect finalise le scaffold…',
          'Workers en parallèle sur les pages…',
          'Merger assemble les fichiers…',
          'Persistance du build…',
        ];
        const phaseIdx = Math.min(Math.floor(attempt / 15), phases.length - 1);
        setLiveStatus({
          phase: 'finalizing',
          label: `${phases[phaseIdx]} (${attempt + 1}/60)`,
        });
        await new Promise(r => setTimeout(r, 3000));
      }
    } catch (_e) { /* best-effort */ }
    return false;
  };

  // Expose handleSend via ref for external triggers (template selection)
  useEffect(() => {
    if (sendRef) sendRef.current = handleSend;
  });

  // Image upload handler
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachedImages(prev => [...prev, {
          preview: reader.result,
          base64: reader.result,
          name: file.name,
        }]);
      };
      reader.readAsDataURL(file);
    });
    if (e.target) e.target.value = '';
  };

  const handleEnhance = async () => {
    if (!input.trim() || enhancing) return;
    setEnhancing(true);
    try {
      const res = await fetch(`${API}/api/enhance-prompt`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: input }),
      });
      if (res.ok) {
        const data = await res.json();
        setInput(data.enhanced_prompt);
      }
    } catch (err) { console.error('Enhance error:', err); }
    finally { setEnhancing(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  };

  // When Aria's edit is approved, mark it in the local map and signal the
  // parent so the preview iframe can refresh once the rebuild kicks in.
  const handleEditApproved = (edit) => {
    setEditMap(prev => ({ ...prev, [edit.edit_id]: { ...edit, status: 'approved' } }));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aria-edit-applied', { detail: { edit } }));
    }
  };

  return (
    <div data-testid="chat-panel" style={{
      flex: '0 0 38%',
      maxWidth: 520,
      minWidth: 340,
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg-depth-1)',
      overflow: 'hidden',
    }}>
      {/* Chat header */}
      <div style={{
        height: 44,
        padding: '0 16px',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-secondary)',
        display: 'flex', alignItems: 'center', gap: 8,
        flexShrink: 0,
        position: 'relative',
      }}>
        <AgentSwitcher
          activeAgent={activeAgent}
          roster={agentRoster}
          onSwitch={onSwitchAgent}
        />
        <span style={{ marginLeft: 'auto' }} />
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1, overflowY: 'auto',
          padding: '12px 14px',
        }}
      >
        {!chatStarted && (
          <div style={{
            textAlign: 'center',
            padding: '40px 16px',
            color: 'var(--text-tertiary)',
            fontSize: 13,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', overflow: 'hidden',
              border: '2px solid var(--accent)', margin: '0 auto 12px',
            }}>
              <img src={AI_AVATAR} alt="AI" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <p style={{ marginBottom: 8, color: 'var(--text-secondary)', fontWeight: 500 }}>
              {ct.describeIdea}
            </p>
            <p style={{ fontSize: 12 }}>
              {ct.aiHelp}
            </p>
          </div>
        )}

        {messages.map(msg => (
          <MessageBubble
            key={msg.message_id}
            message={msg}
            user={user}
            compact
            agentName={activeAgent?.name || 'Aria'}
            editMap={editMap}
            onEditApproved={handleEditApproved}
          />
        ))}
        {isStreaming && streamingContent && (
          <MessageBubble
            message={{ message_id: 'streaming', role: 'assistant', content: streamingContent }}
            isStreaming compact agentName={activeAgent?.name || 'Aria'}
            editMap={editMap}
            onEditApproved={handleEditApproved}
          />
        )}
        {isStreaming && !streamingContent && (
          <LiveStatusBubble status={liveStatus} avatar={AI_AVATAR} />
        )}

        {/* Multi-agent pipeline timeline — shows alongside streaming content
            so the user can watch Architect → Workers → Merger in real time. */}
        {isStreaming && agentPipeline && agentPipeline.length > 0 && (
          <AgentPipelineTimeline agents={agentPipeline} />
        )}

        {/* Modify mode hint — click a section in the preview */}
        {!isStreaming && modifyMode && (
          <div data-testid="modify-hint" style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', margin: '6px 0',
            background: 'rgba(124,58,237,0.08)',
            border: '1px solid rgba(124,58,237,0.2)',
            borderRadius: 0,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <Pencil size={13} style={{ color: '#7C3AED', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#7C3AED', fontWeight: 500 }}>
              {ct.modifyHint}
            </span>
          </div>
        )}

        {/* Modification options — clickable suggestions after AI responds to [MODIFY] */}
        {!isStreaming && pendingModify && messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (() => {
          const lastContent = messages[messages.length - 1].content;
          const optionRegex = /\*\*Option\s*(\d+)[^*]*\*\*[:\s-]*([\s\S]*?)(?=\*\*Option\s*\d|\*\*\s*$|$)/gi;
          const options = [];
          let m;
          while ((m = optionRegex.exec(lastContent)) !== null) {
            const num = m[1];
            const text = m[2].trim().replace(/\n{2,}/g, '\n').slice(0, 300);
            if (text.length > 5) options.push({ num, text });
          }
          if (options.length === 0) return null;
          // Find the section heading from the user's [MODIFY:xxx] message
          const modifyMsg = [...messages].reverse().find(msg => msg.role === 'user' && msg.content.includes('[MODIFY:'));
          const headingMatch = modifyMsg?.content.match(/\[MODIFY:([^\]]+)\]/);
          const sectionHeading = headingMatch ? headingMatch[1] : '';
          return (
            <div data-testid="modify-options" style={{
              display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0 14px',
              animation: 'fadeIn 0.3s ease-out',
            }}>
              {options.map(opt => (
                <button
                  key={opt.num}
                  data-testid={`modify-option-${opt.num}`}
                  onClick={() => {
                    setPendingModify(false);
                    setPendingApply({ heading: sectionHeading });
                    handleSend(`[APPLY] Apply Option ${opt.num} to the "${sectionHeading}" section. Generate ONLY the updated content for this section. Start with the ## heading. Do not include any other sections.`);
                  }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 14px',
                    borderRadius: 0,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-depth-2)',
                    color: 'var(--text-secondary)',
                    fontSize: 12, lineHeight: 1.5,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.background = 'var(--bg-depth-2)'; }}
                >
                  <span style={{
                    flexShrink: 0, width: 22, height: 22,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(124,58,237,0.15)', color: '#7C3AED',
                    fontSize: 11, fontWeight: 700, borderRadius: 0,
                  }}>{opt.num}</span>
                  <span style={{ flex: 1 }}>{opt.text.split('\n')[0]}</span>
                </button>
              ))}
            </div>
          );
        })()}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll button */}
      {showScrollBtn && chatStarted && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'absolute', bottom: 100, right: 20,
            background: 'var(--bg-depth-2)', border: '1px solid var(--border-color)',
            borderRadius: 8, padding: '6px 12px', color: 'var(--text-primary)',
            cursor: 'pointer', fontSize: 12, zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <ArrowDown size={12} />
        </button>
      )}

      {/* Input */}
      <div style={{
        padding: '10px 12px 14px',
        borderTop: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}>
        {/* Selected section chip */}
        {selectedSection && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', marginBottom: 8,
            background: 'rgba(124,58,237,0.08)',
            border: '1px solid rgba(124,58,237,0.25)',
            borderRadius: 0,
            animation: 'fadeIn 0.2s ease-out',
          }}>
            <Pencil size={11} style={{ color: '#7C3AED', flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedSection}
            </span>
            <button onClick={() => { if (onClearSection) onClearSection(); setInputLocked(true); }} style={{
              background: 'none', border: 'none', color: 'rgba(124,58,237,0.5)',
              cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0,
            }}>
              <X size={11} />
            </button>
          </div>
        )}
        {/* Attachments bar: images + URL chip shown together */}
        {(attachedImages.length > 0 || refUrl) && (
          <div style={{
            display: 'flex', gap: 6, padding: '0 0 8px', alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            {attachedImages.map((img, i) => (
              <div key={i} style={{
                position: 'relative', flexShrink: 0,
                width: 48, height: 48, borderRadius: 0,
                overflow: 'hidden', border: '1px solid var(--border-subtle)',
              }}>
                <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={() => setAttachedImages(prev => prev.filter((_, idx) => idx !== i))}
                  style={{
                    position: 'absolute', top: 2, right: 2,
                    width: 14, height: 14, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff',
                  }}
                >
                  <X size={8} />
                </button>
              </div>
            ))}
            {refUrl && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--bg-overlay)', border: '1px solid var(--accent)',
                padding: '4px 10px', fontSize: 11, color: 'var(--accent)',
                fontWeight: 500, maxWidth: 220,
              }}>
                <Globe size={11} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {refUrl.replace(/^https?:\/\/(www\.)?/, '')}
                </span>
                <button onClick={() => setRefUrl('')} style={{
                  background: 'none', border: 'none', color: 'var(--accent)',
                  cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0,
                }}>
                  <X size={10} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* URL input inline */}
        {showUrlInput && !refUrl && (
          <div style={{
            display: 'flex', gap: 6, padding: '0 0 8px', alignItems: 'center',
          }}>
            <Globe size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <input
              data-testid="url-input"
              type="url"
              autoFocus
              placeholder="https://example.com"
              onKeyDown={e => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  setRefUrl(e.target.value.trim());
                  setShowUrlInput(false);
                }
                if (e.key === 'Escape') setShowUrlInput(false);
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                if (val && val.startsWith('http')) {
                  setRefUrl(val);
                }
                setShowUrlInput(false);
              }}
              style={{
                flex: 1, background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)', borderRadius: 0,
                padding: '6px 10px', color: 'var(--text-primary)',
                fontSize: 12, outline: 'none', fontFamily: 'inherit',
              }}
            />
            <button
              onClick={() => setShowUrlInput(false)}
              style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                cursor: 'pointer', padding: 2, display: 'flex',
              }}
            >
              <X size={13} />
            </button>
          </div>
        )}

        <div style={{
          background: inputLocked ? 'var(--bg-depth-2)' : 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 0, padding: 10, position: 'relative',
          opacity: inputLocked ? 0.4 : 1,
          pointerEvents: inputLocked ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        }}>
          <textarea
            ref={textareaRef}
            data-testid="chat-input"
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            disabled={inputLocked}
            placeholder={refUrl || attachedImages.length > 0
              ? ct.placeholderRef
              : ct.placeholder}
            style={{
              width: '100%', background: 'transparent',
              border: 'none', outline: 'none',
              color: 'var(--text-primary)', fontSize: 13,
              lineHeight: 1.5, resize: 'none',
              minHeight: 36, maxHeight: 160,
              padding: '2px 36px 2px 2px',
              fontFamily: 'inherit',
            }}
          />
          <button
            data-testid="send-btn"
            onClick={() => isStreaming ? null : handleSend()}
            disabled={!input.trim() && !isStreaming}
            style={{
              position: 'absolute', right: 12, top: 12,
              background: isStreaming ? 'var(--danger)' : (input.trim() ? 'var(--accent)' : 'var(--bg-depth-3)'),
              border: 'none', borderRadius: 0,
              width: 30, height: 30,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: input.trim() ? '#000' : '#fff',
              cursor: input.trim() || isStreaming ? 'pointer' : 'default',
              transition: 'all 0.3s ease',
              opacity: input.trim() || isStreaming ? 1 : 0.4,
            }}
          >
            {isStreaming ? <Square size={12} /> : <Send size={14} />}
          </button>

          {/* Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 2,
            paddingTop: 6, marginTop: 6,
            borderTop: '1px solid var(--border-subtle)',
          }}>
            <input
              ref={fileInputRef}
              type="file" accept="image/*" multiple
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
            <button
              data-testid="upload-image-btn"
              title="Upload image reference"
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'none', border: 'none', padding: 5,
                color: attachedImages.length > 0 ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => { if (attachedImages.length === 0) e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <Image size={15} />
            </button>

            <button
              data-testid="url-reference-btn"
              title="Add website URL reference"
              onClick={() => { if (refUrl) { setRefUrl(''); } else { setShowUrlInput(!showUrlInput); } }}
              style={{
                background: 'none', border: 'none', padding: 5,
                color: refUrl ? 'var(--accent)' : showUrlInput ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => { if (!refUrl && !showUrlInput) e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <Globe size={15} />
            </button>

            <button
              title="Enhance prompt"
              onClick={handleEnhance}
              disabled={!input.trim() || enhancing}
              style={{
                background: 'none', border: 'none', padding: 5,
                color: 'var(--text-muted)',
                cursor: !input.trim() || enhancing ? 'default' : 'pointer',
                opacity: !input.trim() || enhancing ? 0.3 : 0.7,
                display: 'flex', alignItems: 'center',
                transition: 'all 0.15s',
              }}
            >
              {enhancing ? (
                <div style={{ width: 14, height: 14, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <Sparkles size={15} />
              )}
            </button>

            {(attachedImages.length > 0 || refUrl) && (
              <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 4 }}>
                {[
                  attachedImages.length > 0 ? `${attachedImages.length} img` : '',
                  refUrl ? 'URL' : '',
                ].filter(Boolean).join(' + ')}
              </span>
            )}

            {input.length > 3 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {ct.shiftEnter}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ AgentSwitcher ============ */
function AgentSwitcher({ activeAgent, roster, onSwitch }) {
  const [open, setOpen] = useState(false);
  const current = activeAgent || { agent_id: 'borntobealive-ai', name: 'Aria', color: '#7C3AED' };
  const initials = (current.name || 'AI').split(' ').map(w => w[0]).join('').slice(0, 2);

  const list = (() => {
    const seen = new Set();
    const out = [];
    for (const a of roster || []) {
      if (!seen.has(a.agent_id)) { seen.add(a.agent_id); out.push(a); }
    }
    if (!seen.has('borntobealive-ai')) {
      out.unshift({ agent_id: 'borntobealive-ai', name: 'Aria', color: '#7C3AED', role: 'Builder & Designer' });
    }
    return out;
  })();

  useEffect(() => {
    if (!open) return;
    const onClick = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', onClick), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', onClick); };
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        data-testid="agent-switcher-btn"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 10px 4px 4px', background: 'transparent',
          border: '1px solid transparent', cursor: 'pointer',
          fontFamily: 'inherit', color: 'var(--text-primary)',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: current.color + '20',
          border: `2px solid ${current.color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, color: current.color,
        }}>
          {initials}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{current.name}</span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ opacity: 0.5 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="agent-switcher-menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0,
            minWidth: 260, zIndex: 50,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
            color: 'var(--text-tertiary)', textTransform: 'uppercase',
          }}>
            Switch agent
          </div>
          {list.map(a => {
            const isActive = a.agent_id === current.agent_id;
            const ini = (a.name || 'AI').split(' ').map(w => w[0]).join('').slice(0, 2);
            return (
              <button
                key={a.agent_id}
                onClick={() => { onSwitch && onSwitch(a); setOpen(false); }}
                data-testid={`agent-option-${a.agent_id}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', background: isActive ? 'rgba(124,58,237,0.06)' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: (a.color || '#7C3AED') + '20',
                  border: `2px solid ${a.color || '#7C3AED'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: a.color || '#7C3AED',
                  flexShrink: 0,
                }}>
                  {ini}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{a.name}</div>
                  {a.role && (
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1 }}>
                      {a.role}
                    </div>
                  )}
                </div>
                {isActive && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* =================== Live status bubble =================== */
// Maps backend phases to (Icon, accent color, suggested duration in ms for the
// progress bar). Anything missing falls back to the spinner + raw label.
const PHASE_META = {
  init:               { Icon: Activity,  color: '#7C3AED', estimate: 800 },
  finding_agent:      { Icon: Sparkles,  color: '#06B6D4', estimate: 700 },
  agent_selected:     { Icon: Zap,       color: '#7C3AED', estimate: 400 },
  reading_code:       { Icon: FileCode,  color: '#06B6D4', estimate: 1200 },
  loading_deck:       { Icon: Layers,    color: '#A855F7', estimate: 600 },
  capturing_preview:  { Icon: Camera,    color: '#F97316', estimate: 4000 },
  visuals_ready:      { Icon: Eye,       color: '#22C55E', estimate: 400 },
  scraping_url:       { Icon: Globe,     color: '#06B6D4', estimate: 5000 },
  thinking:           { Icon: Brain,     color: '#A855F7', estimate: 18000 },
  writing:            { Icon: PencilIcon, color: '#7C3AED', estimate: 0 },
  // Multi-agent pipeline phases
  architect_thinking: { Icon: Layers,    color: '#A855F7', estimate: 22000 },
  workers_dispatched: { Icon: Zap,       color: '#F59E0B', estimate: 24000 },
  merging:            { Icon: FileCode,  color: '#06B6D4', estimate: 1500 },
  reconnecting:       { Icon: Activity,  color: '#F97316', estimate: 60000 },
};

function LiveStatusBubble({ status, avatar }) {
  const phase = status?.phase || 'init';
  const label = status?.label || 'Initializing';
  const meta = PHASE_META[phase] || PHASE_META.init;
  const Icon = meta.Icon;

  // Track elapsed time on the current phase so the progress bar fills smoothly.
  // Resets every time the phase changes.
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    setElapsed(0);
    const start = Date.now();
    const id = setInterval(() => setElapsed(Date.now() - start), 120);
    return () => clearInterval(id);
  }, [phase]);

  // Cap the visible progress so it never reaches 100% before completion —
  // logarithmic curve gives a "still working" feel even past the estimate.
  const fillPct = (() => {
    if (!meta.estimate) return 12;
    const ratio = elapsed / meta.estimate;
    return Math.min(96, 8 + Math.log10(1 + ratio * 9) * 90);
  })();

  return (
    <div data-testid="live-status-bubble" style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0',
      animation: 'live-status-fade-in 0.3s ease-out',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', overflow: 'hidden',
        border: `2px solid ${meta.color}`,
        flexShrink: 0,
        boxShadow: `0 0 18px ${meta.color}33`,
        transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
      }}>
        <img src={avatar} alt="AI" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>

      <div style={{
        flex: 1, minWidth: 0, maxWidth: 360,
        padding: '10px 14px',
        background: 'var(--bg-secondary, rgba(255,255,255,0.04))',
        border: '1px solid var(--border, rgba(0,0,0,0.08))',
        borderRadius: 12,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Animated progress bar (top edge) */}
        <div style={{
          position: 'absolute', top: 0, left: 0, height: 2,
          width: `${fillPct}%`,
          background: `linear-gradient(90deg, ${meta.color}, ${meta.color}aa)`,
          boxShadow: `0 0 8px ${meta.color}80`,
          transition: 'width 0.18s linear',
        }} />

        {/* Subtle shimmer overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `linear-gradient(110deg, transparent 35%, ${meta.color}10 50%, transparent 65%)`,
          backgroundSize: '200% 100%',
          animation: 'live-status-shimmer 2.6s linear infinite',
          pointerEvents: 'none',
        }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <div style={{
            width: 22, height: 22, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: meta.color,
            animation: phase === 'writing' ? 'none' : 'live-status-pulse 1.6s ease-in-out infinite',
          }}>
            <Icon size={14} key={phase} style={{
              animation: 'live-status-icon-in 0.35s ease-out',
            }} />
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: 'var(--text-primary, #0a0a0f)',
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              animation: 'live-status-text-in 0.3s ease-out',
            }} key={label}>
              {label}
            </div>
            <div style={{
              fontSize: 9, color: 'var(--text-muted, rgba(0,0,0,0.5))',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              marginTop: 2,
            }}>
              {(elapsed / 1000).toFixed(1)}s
              <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
              {phase.replace(/_/g, ' ')}
            </div>
          </div>

          {/* Three breathing dots — kept as a familiar "AI is working" cue */}
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: '50%',
                background: meta.color,
                opacity: 0.85,
                animation: `live-status-dot 1.1s infinite ${i * 0.18}s ease-in-out`,
              }} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes live-status-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes live-status-text-in {
          from { opacity: 0; transform: translateY(-2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes live-status-icon-in {
          from { opacity: 0; transform: scale(0.6) rotate(-12deg); }
          to   { opacity: 1; transform: scale(1) rotate(0); }
        }
        @keyframes live-status-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.18); }
        }
        @keyframes live-status-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes live-status-dot {
          0%, 100% { opacity: 0.25; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}




/* =================== Multi-agent pipeline timeline =================== */
// Renders the live Architect → Workers → Merger pipeline state. Each agent
// has a colored chip with a status dot and an elapsed timer. Driven by the
// `agent_pipeline` SSE events.
function AgentPipelineTimeline({ agents }) {
  if (!agents || !agents.length) return null;

  const STATUS_COLOR = {
    running: '#F59E0B',
    done:    '#22C55E',
    failed:  '#EF4444',
    pending: '#94A3B8',
  };

  return (
    <div data-testid="agent-pipeline-timeline" style={{
      display: 'flex', alignItems: 'stretch', gap: 8,
      padding: '14px 14px 12px 14px',
      margin: '6px 0',
      background: 'linear-gradient(135deg, rgba(10,10,15,0.03), rgba(124,58,237,0.04))',
      border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 12,
      flexWrap: 'wrap',
      animation: 'pipeline-fade-in 0.35s ease-out',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
        color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase',
        width: '100%', marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Sparkles size={11} style={{ color: '#7C3AED' }} />
        Multi-Agent Pipeline
      </div>
      {agents.map((a, i) => {
        const color = STATUS_COLOR[a.status] || STATUS_COLOR.pending;
        const isActive = a.status === 'running';
        const isDone = a.status === 'done';
        return (
          <React.Fragment key={a.id}>
            <div data-testid={`agent-chip-${a.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 11px',
              background: isActive ? `${color}15` : (isDone ? `${color}10` : 'rgba(0,0,0,0.02)'),
              border: `1px solid ${isActive ? `${color}66` : `${color}33`}`,
              borderRadius: 8,
              transition: 'all 0.35s ease',
              minWidth: 0,
            }}>
              <span style={{ fontSize: 14 }} aria-hidden>{a.icon || (isDone ? '✅' : '⚙️')}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: 'rgba(0,0,0,0.85)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  maxWidth: 140,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{a.name}</div>
                <div style={{
                  fontSize: 9, color: 'rgba(0,0,0,0.45)',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: color,
                    animation: isActive ? 'pipeline-dot 1.1s infinite' : 'none',
                    flexShrink: 0,
                  }} />
                  {a.status}
                  {typeof a.elapsed_s === 'number' && (
                    <span style={{ opacity: 0.6 }}>· {a.elapsed_s.toFixed(1)}s</span>
                  )}
                  {a.model && <span style={{ opacity: 0.5, marginLeft: 4 }}>· {a.model}</span>}
                </div>
              </div>
            </div>
            {i < agents.length - 1 && (
              <ArrowRight size={14} style={{
                color: 'rgba(0,0,0,0.25)', alignSelf: 'center',
              }} />
            )}
          </React.Fragment>
        );
      })}
      <style>{`
        @keyframes pipeline-fade-in {
          from { opacity: 0; transform: translateY(-2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pipeline-dot {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.18); }
        }
      `}</style>
    </div>
  );
}
